<template>
  <div class="w-full flex flex-col scrollbar">
    <div class="fixed-section sticky top-[61px] w-full bg-gray-50 z-20 pt-4">
      <div class="flex flex-col gap-4 lg:flex-row lg:items-center">
        <form @submit.prevent="handleSearch" class="flex flex-1">
          <div class="search-input">
            <div class="search-icon"></div>
            <input
              v-model="state.currentSearch"
              type="text"
              :placeholder="$t('models.model_search_form.model_title')"
              class="review-search-bar"
            />
          </div>
        </form>

        <a href="/profile/campaigns/new" class="hidden lg:inline-flex btn btn-primary py-1 max-h-8 items-center">
          <img src="../../images/icons/upload.svg" class="w-6 h-6" />
          <span class="text-14px font-medium inline-block ml-2">{{ $t('models.model_search_form.upload') }}</span>
        </a>
      </div>

      <div class="mt-8 mb-2 h-5 grid grid-cols-[80px_19fr_19fr_12fr_96px_5fr] xl:grid-cols-[80px_40fr_36fr_36fr_36fr_22fr_32px] gap-x-4 text-12px font-bold text-gray-160">
        <div>{{ $t('campaign_models.index.thumbnail') }}</div>
        <div>{{ $t('campaign_models.index.title') }}</div>
        <div class="xl:hidden">{{ $t('campaign_models.index.model_time_mobile') }}</div>
        <div class="hidden xl:block">{{ $t('campaign_models.index.created_at') }}</div>
        <div class="hidden xl:block">{{ $t('campaign_models.index.launch_time') }}</div>
        <div class="text-center xl:text-left">{{ $t('campaign_models.index.state') }}</div>
      </div>

      <hr class='border-gray-500 my-2'>
    </div>

    <div class="content-section flex-1">
      <div v-if="state.initialLoading || (state.loading && items.length === 0)"
           class="flex-1 flex flex-col items-center justify-center mt-[15%] lg:mt-[20%] 2xl:mt-[15%]">
        <p class="text-16px font-medium text-center">
          <img src="../../images/icons/loading.png" class="w-6 h-6 ml-2 icon loading-icon"/>
        </p>
      </div>

      <div v-else-if="items.length === 0"
           class="flex flex-col items-center justify-center mt-[15%] text-center">
        <img src="../../images/file.svg" class="block w-12 h-12 mb-4" />
        <p class="text-16px font-medium">
          {{ $t('campaign_models.index.no_record', { default: 'No campaigns found.' }) }}
        </p>
      </div>

      <div v-else class="model-container flex-1 overflow-auto scrollbar -mr-2" ref="campaignContainer">
        <campaign-item
          v-for="campaign in items"
          :key="campaign.id"
          :campaign="campaign"
          :is-submitting="submittingCampaigns.has(campaign.id)"
          @campaign-submit="handleSubmitAction"
          @campaign-deleted="handleCampaignDelete"
        />
        <p v-if="state.loading" class="text-16px flex justify-center items-center h-12">
          <img src="../../images/icons/loading.png" class="w-6 h-6 ml-2 icon loading-icon"/>
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent, ref, reactive, computed, onMounted, onUnmounted } from "vue";
import debounce from 'lodash/debounce';

export default defineComponent({
  setup() {
    const scrollListener = ref(null);
    const state = reactive({
      loading: false,
      initialLoading: true,
      itemsCount: 10,
      currentSearch: '',
    });

    const campaignData = reactive({
      items: [],
      totalCount: 0
    });

    const pageInfo = reactive({
      page: 0,
      hasMore: true
    });

    const items = computed(() => campaignData.items);
    const submittingCampaigns = ref(new Set());

    const fetchCampaigns = async (page, perPage) => {
      if (!pageInfo.hasMore || state.loading) return;

      state.loading = true;
      const url = `/api/v1/profile/campaigns`;
      const params = new URLSearchParams({
        page: page + 1,
        per_page: perPage,
        kw: state.currentSearch
      });

      try {
        const response = await fetch(`${url}?${params}`);
        if (response.ok) {
          const data = await response.json();

          campaignData.totalCount = data.pagination.total_count;

          const newItems = data.data;
          newItems.forEach(item => {
            if (!campaignData.items.some(existingItem => existingItem.id === item.id)) {
              campaignData.items.push(item);
            }
          });

          pageInfo.page++;
          pageInfo.hasMore = pageInfo.page < data.pagination.total_pages;

          // 檢查是否需要繼續加載以確保有滾動條
          setTimeout(() => {
            checkAndLoadMore(perPage);
          }, 100);
        }
      } catch (error) {
        console.error('Error fetching campaign data:', error);
        pageInfo.hasMore = false;
      } finally {
        state.loading = false;
        state.initialLoading = false;
      }
    };

    const checkAndLoadMore = (perPage) => {
      if (!pageInfo.hasMore || state.loading) return;

      const documentHeight = document.documentElement.scrollHeight;
      const windowHeight = window.innerHeight;

      if (documentHeight <= windowHeight) {
        fetchCampaigns(pageInfo.page, perPage);
      }
    };

    const scrollToTop = () => {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    };

    const handleSearch = async () => {
      resetState();
      await fetchCampaigns(0, state.itemsCount);
      scrollToTop();
    };

    const handleSubmitAction = async (campaignId, oldState) => {
      if (submittingCampaigns.value.has(campaignId)) return;

      submittingCampaigns.value.add(campaignId);

      try {
        const response = await fetch(`/profile/campaigns/${campaignId}/ask_for_submit`, {
          method: 'POST',
          headers: {
            "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
            "Content-Type": "application/json",
            "Accept": "application/json"
          },
        });

        if (response.ok) {
          // 更新本地狀態
          const index = campaignData.items.findIndex(item => item.id === campaignId);
          if (index !== -1) {
            campaignData.items[index].state = 'pending';
          }
        } else {
          const result = await response.json();
          throw new Error(result.message || 'Submission failed');
        }
      } catch (error) {
        console.error('Error submitting campaign:', error);
      } finally {
        submittingCampaigns.value.delete(campaignId);
      }
    };

    const handleCampaignDelete = (deletedCampaign) => {
      const index = campaignData.items.findIndex(item => item.id === deletedCampaign.id);
      if (index !== -1) {
        campaignData.items.splice(index, 1);
        campaignData.totalCount--;
      }
    };

    const resetState = () => {
      campaignData.items = [];
      campaignData.totalCount = 0;
      pageInfo.page = 0;
      pageInfo.hasMore = true;
    };

    const addScrollListener = () => {
      removeScrollListener();
      scrollListener.value = handleScroll;
      window.addEventListener('scroll', scrollListener.value, { passive: true });
    };

    const removeScrollListener = () => {
      if (scrollListener.value) {
        window.removeEventListener('scroll', scrollListener.value);
        scrollListener.value = null;
      }
    };

    const handleScroll = debounce(() => {
      if (!pageInfo.hasMore) return;
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;

      const scrolledPercentage = (scrollTop / (documentHeight - windowHeight)) * 100;

      if (scrolledPercentage > 30 && !state.loading) {
        fetchCampaigns(pageInfo.page, state.itemsCount);
      }
    }, 100);

    onMounted(async () => {
      try {
        await fetchCampaigns(0, state.itemsCount);
        addScrollListener();
        scrollToTop();
      } catch (error) {
        console.error('Error in onMounted:', error);
      }
    });

    onUnmounted(() => {
      removeScrollListener();
    });

    return {
      items,
      state,
      handleSearch,
      handleSubmitAction,
      handleCampaignDelete,
      submittingCampaigns
    };
  }
});
</script>
